import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { ChevronLeft, ChevronRight } from 'react-bootstrap-icons';
import {
  listIodRoles, saveIodRole, getIodRoleHistory, getIodAvailableMenus,
} from '../../services/iod/iod-admin-service';
import styles from './iod-admin.module.css';

const EMPTY_FORM = { roleCode: '', roleDesc: '', menus: [], status: 'Active' };

export default function IodRoleMaster() {
  const [roles, setRoles]           = useState([]);
  const [allMenus, setAllMenus]     = useState({});  // { parentMenu: [{ menuId, menuName }] }
  const [loading, setLoading]       = useState(false);
  const [showModal, setShowModal]   = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [form, setForm]             = useState(EMPTY_FORM);
  const [errors, setErrors]         = useState({});

  const [showHistory, setShowHistory]   = useState(false);
  const [historyData, setHistoryData]   = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage]   = useState(1);
  const HISTORY_PAGE_SIZE = 10;

  const [writeAccess, setWriteAccess] = useState(false);

  // ── Load ───────────────────────────────────────────────────────────────────
  const loadRoles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listIodRoles();
      setRoles(res.data || []);
    } catch {
      toast.error('Failed to load roles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRoles();
    getIodAvailableMenus()
      .then(res => setAllMenus(res.data || {}))
      .catch(() => {});

    try {
      const sessions = JSON.parse(sessionStorage.getItem('AuthorizedSessions') || '{}');
      const hasWrite = Object.values(sessions).flat()
        .some(m => m.submenu === 'Role and Authorization' && m.write);
      setWriteAccess(hasWrite);
    } catch { setWriteAccess(false); }
  }, [loadRoles]);

  // Flat list of all menu IDs from allMenus
  const allMenuIds = Object.values(allMenus).flat().map(m => m.menuId || m);

  // ── Modal ──────────────────────────────────────────────────────────────────
  const handleAdd = () => {
    setEditingRole(null);
    setForm(EMPTY_FORM);
    setErrors({});
    setShowModal(true);
  };

  const handleEdit = (role) => {
    setEditingRole(role);
    // menus stored as array of menuIds in role.menus or derived from authorizedSessions
    const menuIds = role.menus || extractMenuIds(role.authorizedSessions);
    setForm({
      roleCode: role.roleCode || '',
      roleDesc: role.roleName || role.roleDesc || '',
      menus:    menuIds,
      status:   role.status || 'Active',
    });
    setErrors({});
    setShowModal(true);
  };

  const extractMenuIds = (authorizedSessions) => {
    if (!authorizedSessions) return [];
    return Object.values(authorizedSessions).flat()
      .filter(m => m.read || m.write)
      .map(m => m.menuId || m.submenu);
  };

  const handleClose = () => {
    setShowModal(false);
    setEditingRole(null);
    setForm(EMPTY_FORM);
    setErrors({});
  };

  // ── Toggle menu checkboxes ─────────────────────────────────────────────────
  const toggleMenu = (menuId) => {
    setForm(prev => ({
      ...prev,
      menus: prev.menus.includes(menuId)
        ? prev.menus.filter(m => m !== menuId)
        : [...prev.menus, menuId],
    }));
  };

  const toggleAllMenus = () => {
    setForm(prev => ({
      ...prev,
      menus: prev.menus.length === allMenuIds.length ? [] : [...allMenuIds],
    }));
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (!form.roleDesc.trim()) e.roleDesc = 'Role Description is required';
    if (!form.roleCode.trim()) e.roleCode = 'Role Code is required';
    if (!form.menus.length)    e.menus    = 'Select at least one session';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const payload = {
        roleCode: form.roleCode,
        roleName: form.roleDesc,
        roleDesc: form.roleDesc,
        menus:    form.menus,
        status:   form.status,
        mode:     editingRole ? 'Update' : 'Save',
      };
      const res = await saveIodRole(payload);
      if (res.statusCode === '0000') {
        toast.success(res.statusMsg || 'Saved successfully');
        handleClose();
        loadRoles();
      } else {
        toast.error(res.statusMsg || 'Save failed');
      }
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Save failed');
    } finally {
      setLoading(false);
    }
  };

  // ── Status toggle ──────────────────────────────────────────────────────────
  const handleToggleStatus = async (role) => {
    const newStatus = role.status === 'Active' ? 'Inactive' : 'Active';
    setLoading(true);
    try {
      const res = await saveIodRole({ ...role, status: newStatus, mode: 'Update' });
      if (res.statusCode === '0000') {
        toast.success('Status updated');
        loadRoles();
      } else {
        toast.error(res.statusMsg || 'Update failed');
      }
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Update failed');
    } finally {
      setLoading(false);
    }
  };

  // ── History ────────────────────────────────────────────────────────────────
  const handleHistory = async (role) => {
    setHistoryPage(1);
    setShowHistory(true);
    setHistoryLoading(true);
    try {
      const res = await getIodRoleHistory(role.roleCode);
      setHistoryData((res.data || []).sort((a, b) => new Date(b.modifiedDate) - new Date(a.modifiedDate)));
    } catch {
      toast.error('Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const historySlice = historyData.slice((historyPage - 1) * HISTORY_PAGE_SIZE, historyPage * HISTORY_PAGE_SIZE);
  const historyTotalPages = Math.ceil(historyData.length / HISTORY_PAGE_SIZE) || 1;

  // ── Render menu checkboxes grouped by parent ───────────────────────────────
  const renderMenuCheckboxes = () => {
    const groups = Object.entries(allMenus);
    if (!groups.length) return <div className={styles.emptyState}>Loading menus…</div>;
    return (
      <div>
        <label className={styles.selectAllRow}>
          <input
            type="checkbox"
            checked={form.menus.length === allMenuIds.length && allMenuIds.length > 0}
            onChange={toggleAllMenus}
          />
          Select All Sessions
        </label>
        {errors.menus && <div className={styles.errorText}>{errors.menus}</div>}
        {groups.map(([parent, items]) => (
          <div key={parent} style={{ marginBottom: '12px' }}>
            <div style={{ fontWeight: 600, fontSize: '13px', color: '#810055', marginBottom: '6px' }}>
              {parent}
            </div>
            <div className={styles.menuGrid}>
              {(Array.isArray(items) ? items : []).map(item => {
                const id = item.menuId || item;
                const name = item.menuName || item;
                return (
                  <label key={id} className={styles.checkItem}>
                    <input
                      type="checkbox"
                      checked={form.menus.includes(id)}
                      onChange={() => toggleMenu(id)}
                      data-testid={`menu-${id}`}
                    />
                    {name}
                  </label>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={styles.pageWrap}>
      {loading && (
        <div className={styles.spinnerOverlay}>
          <Spinner animation="border" style={{ color: '#810055' }} />
        </div>
      )}

      <div className={styles.pageTitle}>Role & Authorization Master</div>
      <div className={styles.pageSubTitle}>Add and Edit Role Details</div>

      <div className={styles.toolbar}>
        <div className={styles.toolbarLeft}>
          {writeAccess && (
            <button className={styles.btnPrimary} onClick={handleAdd} data-testid="btn-add-role">
              Add Role
            </button>
          )}
          <span className={styles.hint}>* Double-click row to edit</span>
        </div>
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Status</th>
              <th>Role Code</th>
              <th>Role Name</th>
              <th>Sessions</th>
              <th>Modified By</th>
              <th>Modified On</th>
              {writeAccess && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {roles.length === 0 ? (
              <tr><td colSpan={7} className={styles.emptyState}>No roles found</td></tr>
            ) : roles.map(r => (
              <tr key={r.roleCode} onDoubleClick={() => writeAccess && handleEdit(r)}>
                <td>
                  <span className={r.status === 'Active' ? styles.badgeActive : styles.badgeInactive}>
                    {r.status}
                  </span>
                </td>
                <td>{r.roleCode}</td>
                <td>{r.roleName || r.roleDesc}</td>
                <td style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.authorizedSessions
                    ? Object.values(r.authorizedSessions).flat().filter(m => m.read).map(m => m.submenu).join(', ')
                    : (r.menus || []).join(', ')}
                </td>
                <td>{r.modifiedBy}</td>
                <td>{r.modifiedDate ? new Date(r.modifiedDate).toLocaleDateString('en-IN') : ''}</td>
                {writeAccess && (
                  <td>
                    <button className={styles.actionBtn} onClick={() => handleEdit(r)}>Edit</button>
                    <button className={styles.actionBtn} onClick={() => handleToggleStatus(r)}>
                      {r.status === 'Active' ? 'Deactivate' : 'Activate'}
                    </button>
                    <button className={styles.actionBtn} onClick={() => handleHistory(r)}>History</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Add / Edit Modal ── */}
      <Modal show={showModal} onHide={handleClose} size="lg" backdrop="static">
        <Modal.Header closeButton style={{ background: '#810055', color: '#fff' }}>
          <Modal.Title style={{ fontSize: '16px' }}>
            {editingRole ? `Edit Role — ${editingRole.roleCode}` : 'Add New Role'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Role Description *</label>
            <input
              className={`${styles.formControl} ${errors.roleDesc ? styles.error : ''}`}
              value={form.roleDesc}
              onChange={e => setForm(p => ({ ...p, roleDesc: e.target.value }))}
              placeholder="Role Description"
              data-testid="input-roleDesc"
            />
            {errors.roleDesc && <div className={styles.errorText}>{errors.roleDesc}</div>}
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Role Code *</label>
            <input
              className={`${styles.formControl} ${errors.roleCode ? styles.error : ''}`}
              value={form.roleCode}
              onChange={e => setForm(p => ({ ...p, roleCode: e.target.value }))}
              readOnly={!!editingRole}
              placeholder="e.g. DEALER"
              data-testid="input-roleCode"
            />
            {errors.roleCode && <div className={styles.errorText}>{errors.roleCode}</div>}
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Status</label>
            <select
              className={styles.formControl}
              value={form.status}
              onChange={e => setForm(p => ({ ...p, status: e.target.value }))}
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>

          <hr />
          <div style={{ fontWeight: 600, marginBottom: '8px', color: '#333' }}>Role Authorization</div>
          {renderMenuCheckboxes()}
        </Modal.Body>
        <Modal.Footer>
          <button className={styles.btnSecondary} onClick={handleClose}>Cancel</button>
          <button className={styles.btnPrimary} onClick={handleSave} disabled={loading}>
            {loading ? 'Saving…' : 'Save'}
          </button>
        </Modal.Footer>
      </Modal>

      {/* ── History Modal ── */}
      <Modal show={showHistory} onHide={() => setShowHistory(false)} size="xl" backdrop="static">
        <Modal.Header closeButton style={{ background: '#810055', color: '#fff' }}>
          <Modal.Title style={{ fontSize: '16px' }}>Role History</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {historyLoading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spinner animation="border" style={{ color: '#810055' }} />
            </div>
          ) : (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Role Code</th><th>Role Name</th><th>Status</th>
                    <th>Sessions</th><th>Modified By</th><th>Modified On</th>
                  </tr>
                </thead>
                <tbody>
                  {historySlice.map((h, i) => (
                    <tr key={i}>
                      <td>{h.roleCode}</td>
                      <td>{h.roleName || h.roleDesc}</td>
                      <td>
                        <span className={h.status === 'Active' ? styles.badgeActive : styles.badgeInactive}>
                          {h.status}
                        </span>
                      </td>
                      <td style={{ fontSize: '12px' }}>
                        {h.authorizedSessions
                          ? Object.values(h.authorizedSessions).flat().filter(m => m.read).map(m => m.submenu).join(', ')
                          : (h.menus || []).join(', ')}
                      </td>
                      <td>{h.modifiedBy}</td>
                      <td>{h.modifiedDate ? new Date(h.modifiedDate).toLocaleDateString('en-IN') : ''}</td>
                    </tr>
                  ))}
                  {historySlice.length === 0 && (
                    <tr><td colSpan={6} className={styles.emptyState}>No history found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
        <div className={styles.paginationBar}>
          <span>{(historyPage - 1) * HISTORY_PAGE_SIZE + 1}–{Math.min(historyPage * HISTORY_PAGE_SIZE, historyData.length)} of {historyData.length}</span>
          <div className={styles.paginationBtns}>
            <ChevronLeft
              size={18}
              style={{ cursor: historyPage === 1 ? 'default' : 'pointer', color: historyPage === 1 ? '#ccc' : '#333' }}
              onClick={() => historyPage > 1 && setHistoryPage(p => p - 1)}
            />
            <span>Page {historyPage} of {historyTotalPages}</span>
            <ChevronRight
              size={18}
              style={{ cursor: historyPage === historyTotalPages ? 'default' : 'pointer', color: historyPage === historyTotalPages ? '#ccc' : '#333' }}
              onClick={() => historyPage < historyTotalPages && setHistoryPage(p => p + 1)}
            />
          </div>
        </div>
        <Modal.Footer>
          <button className={styles.btnSecondary} onClick={() => setShowHistory(false)}>Close</button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
